// Parent Homework surface controller (SC-H1a).
// Owns ONE DOM subtree mounted into a cached persistent host (parent-view.js keeps the
// host across re() so this subtree is never torn down). ONE fetch pair on entry
// (loadHomeworkForParent + loadCompletionsV2, existing 90-day bound). Tab switches and
// realtime completion echoes patch in place — no re(), no refetch.
//
// Bridge (window.HUD_HOMEWORK): mountHomeworkParent / refreshHomeworkParent / destroyHomeworkParent.

import { loadHomeworkForParent, loadCompletionsV2, saveCompletionV2, exerciseSlotsOn, resolveExerciseSchedule } from '../data.js';
import { injectHwpStyles } from './styles.js';
import { renderTodayInto, todaySlotTotal } from './today.js';
import { renderAllHomeworkInto } from './all-homework.js';
import { renderBoardInto } from './hw-board.js';
import { renderExerciseInto } from './exercise.js';

let _st = null; // controller state (module singleton)
// Notes SC1e: the last data SHOWN per child (in memory only; a sign-out hard-reloads the page). A fresh mount whose
// fetch fails (offline child switch) shows this instead of an empty list.
const _lastShown = new Map();

function _ninetyDaysAgo() { const d = new Date(); d.setDate(d.getDate() - 90); return d; }
function _buildCompMap(comps) { const m = {}; (comps || []).forEach(c => { m[c.homework_exercise_id + ':' + c.scheduled_date + ':' + (c.slot || '')] = c; }); return m; }

// Per-path signed-URL cache: re-renders (tab switch, board↔all, realtime patch) reuse URLs so a
// view switch is zero-request. Entries expire at 780s (13 min, < the 900s signed-URL TTL, SC-H1b)
// and re-sign lazily on next render — no proactive timers.
const _SIGN_TTL_MS = 780000;
const _signCache = new Map(); // path -> { url, ts }
function _sign(path) {
  if (!path) return Promise.resolve(null);
  const hit = _signCache.get(path);
  if (hit && (Date.now() - hit.ts) < _SIGN_TTL_MS) return Promise.resolve(hit.url);
  const H = window.HUD || {};
  let p = null;
  try {
    if (typeof H._signFilePool === 'function') p = H._signFilePool(path);
    else if (H.SB?.signFile) p = H.SB.signFile(path);
  } catch (_) {}
  if (!p) return Promise.resolve(null);
  return p.then(url => { if (url) _signCache.set(path, { url, ts: Date.now() }); return url || null; }, () => null);
}

// SC-H2: a tile tap opens the in-surface exercise screen — no re(), no fetch.
function _onTileTap(hwId, exId, slot) {
  if (!_st) return;
  _st.exercise = { hwId, exId, slot, origin: _st.subtab === 'board' ? 'board' : 'today' };
  _st.subtab = 'exercise';
  _renderContent();
}

// Save handler (called from the exercise screen's celebration + the parent sheet). Guards + writes
// via saveCompletionV2, then optimistically patches compMap (tile/ring/strip) and returns {ok}.
// SC-H4: navigation is NOT done here — the caller decides (celebration Next → advance to the next
// open slot; sheet direct → origin). The echo is suppressed by the tracker inside
// logExerciseCompletion — no reload.
async function _onSaveCompletion(payload) {
  const H = window.HUD || {};
  if (!_st) return { error: true };
  const hw = _st.homeworks.find(h => h.id === payload.hwId);
  const ex = hw && (hw.exercises || []).find(e => e.id === payload.exId);
  if (!hw || !ex) return { error: true };
  let res;
  try {
    res = await saveCompletionV2({
      homework: hw, exercise: ex, scheduledDate: payload.scheduledDate, slot: payload.slot,
      status: payload.status, note: payload.note, photoPath: payload.photoPath, mediaType: payload.mediaType,
      actualValue: payload.actualValue, actualSeconds: payload.actualSeconds, childRating: payload.childRating,
      childId: _st.childId, existingCompletionId: payload.existingCompletionId, previousStatus: payload.previousStatus,
    });
  } catch (e) {
    console.error('❌ [hwp] save:', e);
    H.toast?.(H.T ? H.T('hw3_save_fail') : 'Could not save.', 'error');
    return { error: true };
  }
  if (res?.slotInvalid) { H.toast?.(H.T ? H.T('hw3_save_fail') : 'Could not save.', 'error'); return { error: true }; }
  if (res?.stale) { H.toast?.(H.T ? H.T('hw3_changed_elsewhere') : 'Changed elsewhere.', 'error'); return { error: true }; }

  // Optimistic patch — build the completion row from the form + the returned id.
  const key = payload.exId + ':' + payload.scheduledDate + ':' + (payload.slot || '');
  const row = {
    id: res?.id || _st.compMap[key]?.id || null,
    homework_exercise_id: payload.exId, homework_id: payload.hwId, child_id: _st.childId,
    household_id: hw.household_id, scheduled_date: payload.scheduledDate, slot: payload.slot || null,
    status: payload.status, note: payload.note || null,
    actual_value: payload.actualValue ?? null, actual_seconds: payload.actualSeconds ?? null,
    child_rating: payload.childRating ?? null, photo_path: payload.photoPath || null,
    media_type: payload.photoPath ? (payload.mediaType || 'image') : null,
    photo_url: null, logged_by: H.session?.id || null,
  };
  _st.compMap[key] = row;
  const ci = _st.completions.findIndex(c => c.id && row.id && c.id === row.id);
  if (ci >= 0) _st.completions[ci] = row; else _st.completions.push(row);

  H.toast?.(H.T ? H.T('hw5_saved') : 'Saved ✓');
  // caller navigates (SC-H4): celebration Next advances; sheet direct → origin. `row` lets the exercise
  // screen target the saved row directly (QA P0-3) — _st.compMap can be replaced by a refresh meanwhile.
  return { ok: true, row };
}

export async function mountHomeworkParent(host, ctx) {
  injectHwpStyles();
  const H = window.HUD || {};

  // Idempotent re-entry: re() re-invokes this with the SAME cached host. Don't refetch, don't log.
  if (_st && _st.mounted && _st.host === host && _st.childId === ctx.childId) {
    if (!_st.loading) _renderContent();
    return host;
  }

  // Fresh mount (first entry or child switch). Preserve the view state across a same-child re-mount.
  const sameChild = _st && _st.childId === ctx.childId;
  const prevSubtab = sameChild ? _st.subtab : 'today';
  const prevBoardHwId = sameChild ? _st.boardHwId : null;
  const prevAllSubtab = sameChild ? _st.allSubtab : 'active';
  if (_st && !_st.loading) _lastShown.set(_st.childId, { homeworks: _st.homeworks, completions: _st.completions }); // Notes SC1e
  _st = { host, childId: ctx.childId, isWeb: ctx.isWeb, homeworks: [], completions: [], compMap: {}, subtab: prevSubtab, boardHwId: prevBoardHwId, allSubtab: prevAllSubtab, mounted: true, loading: true };

  host.innerHTML = '<div class="hwp"><div class="hwp-loading">' + (H.T ? H.T('btn_loading') : 'Loading…') + '</div></div>';

  const childId = ctx.childId;
  try {
    // Notes SC1e: strict — a failed read shows what this child last showed (or the empty list, as before); never a
    // half-loaded list (tasks without exercises / completions).
    let homeworks, completions;
    try {
      [homeworks, completions] = await Promise.all([
        loadHomeworkForParent(childId, { strict: true }),
        loadCompletionsV2(childId, _ninetyDaysAgo(), { strict: true }),
      ]);
    } catch (e) {
      console.error('❌ [hwp] fetch (last shown kept):', e);
      const last = _lastShown.get(childId);
      homeworks = last ? last.homeworks : []; completions = last ? last.completions : [];
    }
    if (!_st || _st.childId !== childId) return host; // child switched mid-fetch — abandon
    _st.homeworks = homeworks || [];
    _st.completions = completions || [];
    _st.compMap = _buildCompMap(_st.completions);
    _st.loading = false;
    // SC1 (print QR deep-link): open a specific exercise on a fresh mount, then consume the one-shot.
    if (ctx.initialExercise && ctx.initialExercise.exId) {
      const _ie = ctx.initialExercise;
      const hw = _st.homeworks.find(h => h.id === _ie.hwId) || _st.homeworks.find(h => (h.exercises || []).some(e => e.id === _ie.exId));
      const ex = hw && (hw.exercises || []).find(e => e.id === _ie.exId);
      if (hw && ex) {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const slot = _ie.slot || exerciseSlotsOn(hw, ex, today)[0] || resolveExerciseSchedule(hw, ex).timeSlots[0] || 'morning';
        _st.exercise = { hwId: hw.id, exId: ex.id, slot, origin: 'today' };
        _st.subtab = 'exercise';
      }
      try { if (H.S) H.S._hwInitialExercise = null; } catch (_) {}
    }
    _renderContent();
  } catch (e) {
    console.error('❌ [hwp] fetch:', e);
    if (_st && _st.childId === childId) {
      _st.loading = false;
      host.innerHTML = '<div class="hwp"><div class="hwp-loading">' + (H.T ? H.T('err_generic') : 'Could not load.') + '</div></div>';
    }
  }
  return host;
}

async function _reloadCompletions() {
  if (!_st) return;
  let comps;
  // Notes SC1e: a failed read keeps the completions already shown.
  try { comps = await loadCompletionsV2(_st.childId, _ninetyDaysAgo(), { strict: true }); }
  catch (e) { console.error('❌ [hwp] reload completions (kept):', e); return; }
  if (!_st) return;
  _st.completions = comps || [];
  _st.compMap = _buildCompMap(_st.completions);
}

// Called by the homework realtime handlers (index.html) when this surface is mounted.
//  - { completion: row, event } → merge one row, no fetch (co-parent echo).
//  - { reload:true }            → refetch both reads (a homework_tasks change).
export async function refreshHomeworkParent(payload) {
  if (!_st || !_st.mounted) return;
  if (payload && payload.completion) {
    const c = payload.completion;
    if (!c || !c.homework_exercise_id) return;
    const key = c.homework_exercise_id + ':' + c.scheduled_date + ':' + (c.slot || '');
    if (payload.event === 'DELETE') {
      delete _st.compMap[key];
      _st.completions = _st.completions.filter(x => x.id !== c.id);
    } else {
      _st.compMap[key] = c;
      const i = _st.completions.findIndex(x => x.id === c.id);
      if (i >= 0) _st.completions[i] = c; else _st.completions.push(c);
    }
    if (!_st.loading) _renderContent();
    return;
  }
  // reload path (homework changed)
  try {
    // Notes SC1e: strict — a failed read throws to the catch below, which keeps the list already shown.
    const [homeworks, completions] = await Promise.all([
      loadHomeworkForParent(_st.childId, { strict: true }),
      loadCompletionsV2(_st.childId, _ninetyDaysAgo(), { strict: true }),
    ]);
    if (!_st) return;
    _st.homeworks = homeworks || [];
    _st.completions = completions || [];
    _st.compMap = _buildCompMap(_st.completions);
    if (!_st.loading) _renderContent();
  } catch (e) { console.error('❌ [hwp] refresh reload (kept):', e); }
}

export function destroyHomeworkParent() { _st = null; _lastShown.clear(); }

// True when the parent Today surface owns the screen — the realtime
// handlers use this to decide between refreshHomeworkParent() and the generic _debouncedRefresh().
export function isHomeworkParentMounted() {
  return !!(_st && _st.mounted && !_st.loading);
}

function _renderContent() {
  if (!_st) return;
  const H = window.HUD || {};
  const host = _st.host;
  host.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'hwp';
  host.appendChild(wrap);

  const todayCount = todaySlotTotal(_st.homeworks, _st.compMap).total;
  const allCount = (_st.homeworks || []).filter(h => !h.is_paused).length;

  if (_st.subtab === 'exercise') {
    const ex = _st.exercise || {};
    renderExerciseInto(wrap, {
      homeworks: _st.homeworks, compMap: _st.compMap, childId: _st.childId, H,
      hwId: ex.hwId, exId: ex.exId, slot: ex.slot, origin: ex.origin,
      onBack: () => { _st.subtab = ex.origin || 'today'; _renderContent(); },
      onOpenExercise: (hwId, exId, slot) => { _st.exercise = { hwId, exId, slot, origin: ex.origin }; _renderContent(); },
      onSave: _onSaveCompletion,
      sign: _sign,
    });
    return;
  }

  if (_st.subtab === 'board') {
    renderBoardInto(wrap, {
      homeworks: _st.homeworks, compMap: _st.compMap, childId: _st.childId, H,
      boardHwId: _st.boardHwId,
      onBackToAll: () => { _st.subtab = 'all'; _renderContent(); },
      onTileTap: _onTileTap,
      sign: _sign,
    });
    return;
  }

  if (_st.subtab === 'all') {
    renderAllHomeworkInto(wrap, {
      homeworks: _st.homeworks, compMap: _st.compMap, childId: _st.childId, H,
      todayCount, allCount, allSubtab: _st.allSubtab,
      onGoToday: () => { _st.subtab = 'today'; _renderContent(); },
      onSetAllSubtab: (k) => { _st.allSubtab = k; _renderContent(); },
      onOpenBoard: (hwId) => { _st.subtab = 'board'; _st.boardHwId = hwId; _renderContent(); },
    });
    return;
  }

  renderTodayInto(wrap, {
    homeworks: _st.homeworks, compMap: _st.compMap, childId: _st.childId, isWeb: _st.isWeb, H,
    allCount,
    onTileTap: _onTileTap,
    onGoAll: () => { _st.subtab = 'all'; _renderContent(); },
    sign: _sign,
  });
}
